import { Component, Inject, OnInit, ViewChild } from '@angular/core';
import { MAT_DIALOG_DATA, MatDialogRef, MatDialogConfig, MatDialog } from '@angular/material/dialog';
import { MatSnackBar } from '@angular/material/snack-bar';
import { MatSelect } from '@angular/material/select';
import { DataTransformerConfig } from 'src/app/domainObjects/dataTransformConfig';
import { ConfigService } from 'src/app/services/config/config.service';
import { BaseComponent } from 'src/app/base/base.component';
import { ErrorReportingService } from 'src/app/services/errorReporting/error-reporting.service';
import { ScheduleFrequencyType, ScheduleDay, ConfigSchedule } from 'src/app/services/scheduler/scheduler.service';
import { OptionItem } from 'src/app/model/optionItem';

@Component({
    selector: 'app-new-schedule-dialog',
    templateUrl: './new-schedule-dialog.component.html',
    styleUrls: ['./new-schedule-dialog.component.css'],
    standalone: false
})
export class NewScheduleDialogComponent extends BaseComponent implements OnInit {
  formTitle = 'New Pipeline Schedule';
  configScheduleId?: number;
  configId?: number;
  configGuid?: string;
  configs: DataTransformerConfig[];
  name: string;
  startTime: any;
  startDate: Date;
  frequencyType: ScheduleFrequencyType;
  frequencyTypes = [
    new OptionItem(ScheduleFrequencyType.Daily, 'Daily'),
    new OptionItem(ScheduleFrequencyType.Weekly, 'Weekly'),
    new OptionItem(ScheduleFrequencyType.Monthly, 'Monthly'),
    new OptionItem(ScheduleFrequencyType.Once, 'Once')];
  selectedDays: string[];
  daysOptions = [
    new OptionItem(ScheduleDay.Sunday, 'Sunday'),
    new OptionItem(ScheduleDay.Monday, 'Monday'),
    new OptionItem(ScheduleDay.Tuesday, 'Tuesday'),
    new OptionItem(ScheduleDay.Wednesday, 'Wednesday'),
    new OptionItem(ScheduleDay.Thursday, 'Thursday'),
    new OptionItem(ScheduleDay.Friday, 'Friday'),
    new OptionItem(ScheduleDay.Saturday, 'Saturday')];
  retryAttempts?: number;
  retryInterval?: number;

  constructor(
    @Inject(MAT_DIALOG_DATA) public data,
    public dialogRef: MatDialogRef<NewScheduleDialogComponent>,
    matDialog: MatDialog,
    errorLogger: ErrorReportingService,
    private configService: ConfigService,
    private snackBar: MatSnackBar) {
    super(errorLogger, matDialog);
  }
  @ViewChild('daysSelect') daysSelect: MatSelect;

  async ngOnInit(): Promise<void> {
    const organizationId = 1; // TODO: Use selected organization
    this.configs = await this.configService.getAllConfigs(organizationId);

    // Load the schedule if we are editing
    const scheduleToEdit: ConfigSchedule = this.data.schedule;
    if (scheduleToEdit) {
      this.formTitle = 'Edit Config Schedule';
      this.bindData(scheduleToEdit);
    }
  }

  bindData(scheduleToEdit: ConfigSchedule): void {
    this.configScheduleId = scheduleToEdit.id;
    this.name = scheduleToEdit.scheduleName;
    this.configId = scheduleToEdit.configId;
    this.configGuid = scheduleToEdit.configGuid;
    this.frequencyType = scheduleToEdit.frequencyType;
    this.startDate = scheduleToEdit.activeStartDate;
    this.startTime = this.convertSqlTimeToString(scheduleToEdit.activeStartTime);
    this.retryAttempts = scheduleToEdit.retryAttempts;
    this.retryInterval = scheduleToEdit.retryInterval;

    this.selectedDays = [];
    if (scheduleToEdit.frequencyType === ScheduleFrequencyType.Weekly || scheduleToEdit.frequencyType === ScheduleFrequencyType.Monthly) {
      this.daysOptions.forEach( option => {
        if (this.isDayBitSet(scheduleToEdit.days, option.value)) {
          this.selectedDays.push(this.getDayStringFromScheduleDay(option.value));
        }
      });
    } else {
      this.daysSelect.disabled = true;
    }
  }

  private isDayBitSet(daysFlag: ScheduleDay, day: ScheduleDay): boolean {
    // tslint:disable-next-line:no-bitwise
    return ((day & daysFlag) === day);
  }

  save(): void {
    const schedule = new ConfigSchedule();
    schedule.id = this.configScheduleId;
    schedule.configId = this.configId;
    schedule.configGuid = this.configGuid;
    schedule.activeStartDate = this.startDate;
    schedule.activeStartTime = this.convertTimeStringToSQLTime(this.startTime);
    schedule.scheduleName = this.name;
    schedule.days = this.getDays();
    schedule.frequencyType = this.frequencyType;
    schedule.retryAttempts = this.retryAttempts;
    schedule.retryInterval = this.retryInterval;

    //console.log(schedule);

    if (!schedule.configGuid) {
      this.displaySnackbar('You must select a config');
      return;
    }
    if (!schedule.scheduleName) {
      this.displaySnackbar('You must enter a schedule name');
      return;
    }
    if (!schedule.frequencyType) {
      this.displaySnackbar('You must select a frequency type');
      return;
    }
    if (!schedule.activeStartTime) {
      this.displaySnackbar('You must select an active start time');
      return;
    }

    this.dialogRef.close({newSchedule: schedule});
  }

  cancel(): void {
    // nothing to do...
  }

  onFrequencyTypeChanged(event): void {
    this.daysSelect.disabled = event.value === ScheduleFrequencyType.Daily;
  }

  private displaySnackbar(message: string): void {
    this.snackBar.open(message, 'Dismiss', {
      duration: 5000
    });
  }

  private getDays(): number {
    let days = 0;
    if (this.frequencyType === ScheduleFrequencyType.Daily || this.frequencyType === ScheduleFrequencyType.Once) {
      days = 1;
      return days;
    }

    this.selectedDays.forEach((dayString) => {
      // tslint:disable-next-line:no-bitwise
      days |= this.getScheduleDayFromDayString(dayString);
    });

    //console.log(`Days value is ${days}`);
    return days;
  }

  private convertSqlTimeToString(sqlTime: number): string {
    // HHMMSS
    const sqlString = sqlTime.toString();
    if (sqlString.length > 5) {
      const hr = sqlString.substr(0, 2);
      const min = sqlString.substr(2, 2);
      const hr24Hr = Number(hr);
      const ampm = hr24Hr > 11 ? 'PM' : 'AM';
      return `${hr}:${min} ${ampm}`;
    }

    const hr = sqlString.substr(0, 1);
    const min = sqlString.substr(1, 2);
    const hr24Hr = Number(hr);
    const ampm = hr24Hr > 11 ? 'PM' : 'AM';
    return `${hr}:${min} ${ampm}`;
  }

  private convertTimeStringToSQLTime(timeString: string): number {
    if (!timeString) { return null; }

    //console.log(`timeString = ${timeString}`);
    let isPM = true;

    if (timeString.endsWith('AM')) {
      isPM = false;
    }

    let sqlTimeString = timeString.replace(':', '');
    sqlTimeString = sqlTimeString.substr(0, sqlTimeString.length - 2).trim();
    let hr =  Number(sqlTimeString.substr(0, sqlTimeString.length > 3 ? 2 : 1));
    const min = Number(sqlTimeString.substr(sqlTimeString.length > 3 ? 2 : 1, 2));
    if (isPM && hr !== 12) {
      hr += 12;
    }
    if (hr === 24) {
      sqlTimeString = `${min}00`;
    } else {
      sqlTimeString = min > 9 ? `${hr}${min}00` : `${hr}0${min}00`;
    }

    return Number(sqlTimeString);
  }

  private getScheduleDayFromDayString(dayString: string): ScheduleDay {
    switch ( dayString ) {
      case 'Sunday': return ScheduleDay.Sunday;
      case 'Monday': return ScheduleDay.Monday;
      case 'Tuesday': return ScheduleDay.Tuesday;
      case 'Wednesday': return ScheduleDay.Wednesday;
      case 'Thursday': return ScheduleDay.Thursday;
      case 'Friday': return ScheduleDay.Friday;
      case 'Saturday': return ScheduleDay.Saturday;
      default: return null;
    }
  }

  private getDayStringFromScheduleDay(day: ScheduleDay): string {
    switch ( day ) {
      case ScheduleDay.Sunday: return 'Sunday';
      case ScheduleDay.Monday: return 'Monday';
      case ScheduleDay.Tuesday: return 'Tuesday';
      case ScheduleDay.Wednesday: return 'Wednesday';
      case ScheduleDay.Thursday: return 'Thursday';
      case ScheduleDay.Friday: return 'Friday';
      case ScheduleDay.Saturday: return 'Saturday';
    }
  }

  onDaysCheckedChanged(event): void {
    //console.log('onDaysCheckedChanged');
    //console.log(event.value);
    //item.isSelected = !item.isSelected;
  }
}


